/*
 * Copyright (c) 2024-2025 Nodals AI Ltd
 * All rights reserved.
 */

import { Opportunity } from '../../auction';
import { Campaign } from '../../campaigns/campaign';
import { EventDispatcher } from '../../events';
import { Logger } from '../../logger';
import { Facts } from '../../rules';
import { AdIdType, CampaignType } from '../../types';
import { clone, debugPixel, generateUUID, getGlobalPrebidObj } from '../../util';
import { DIMENSIONS_UNKNOWN, Visibility } from './constants';
import { PlacementEvents } from './events';

import type {
  AdAssignedKeyValues,
  CampaignData,
  CampaignEligibleAds,
  KeyValue,
  PlacementAssignedKeyValues,
  PlacementSize,
  RenderedAdData,
} from '../../types/index';
import type {
  PrebidAdRenderFailedData,
  PrebidAdRenderSucceededData,
  PrebidAdUnit,
} from '../../types/prebid';
import type { SiteConfig } from '../config';

const logger = Logger.getInstance();
const MAX_STORED_OPPORTUNITIES = 5;

abstract class Placement extends EventDispatcher<PlacementEvents> {
  private _adUnit: PrebidAdUnit;
  private campaignData: CampaignData[];
  private _campaigns: Campaign[] = [];
  private _facts: Facts = {};
  private _visibility: Visibility = Visibility.UNKNOWN;
  private _keyValues: KeyValue[] = [];
  private _opportunities: Opportunity[] = [];
  private _supportedSizes: PlacementSize[] = [];
  private adUnitTransformer: (adUnitCode: string) => string;
  private impressionDeliveryId: string | null = null;

  protected renderedAdData: RenderedAdData | null = null;
  protected partialAdData: Partial<RenderedAdData> | null = null;
  protected currentDeliveryId: string | null = null;
  protected slot: any = null;
  protected adInteractionLoggedForOpportunity: boolean = false;
  protected slotRenderedForOpportunity: boolean = false;
  protected enablePrebidRenderListenerForOpportunity: boolean = false;
  protected prebidAdRenderedForOpportunity: boolean = false;
  protected slotViewedForOpportunity: boolean = false;
  protected wrappingDomElement: HTMLElement | null = null;
  protected viewableImpressionMissed: boolean = false;
  protected siteConfig: SiteConfig;
  private slotDetectionIntervalId: NodeJS.Timeout | null = null;
  private slotDetectionAttempts: number = 0;
  private readonly maxSlotDetectionAttempts: number = 20;
  private readonly slotDetectionIntervalMs: number = 100;

  constructor(
    adUnit: PrebidAdUnit,
    adUnitTransformer: (adUnitCode: string) => string,
    campaignData: CampaignData[],
    siteConfig: SiteConfig
  ) {
    super();
    this._adUnit = adUnit;
    this.adUnitTransformer = adUnitTransformer;
    this.campaignData = campaignData;
    this.siteConfig = siteConfig;
  }

  public init(): boolean {
    this.initialiseSupportedSizes();
    this.determineSlot();
    this.setFacts();
    this.initialiseCampaigns();
    if (!this.slot) {
      logger.warn(`Placement for adUnit ${this.adUnitCode} has no valid slot`);
      return false;
    }
    return true;
  }

  public determineSlot(): void {
    if (!this.slot) {
      this.setSlot();
      if (this.slot) {
        this.clearSlotDetectionInterval();
        this.setWrappingDomElement();
        this.determineVisibility();
        this.setSupportedSizes();
        this.addListeners();
      } else {
        this.startSlotDetectionInterval();
      }
    }
  }

  private startSlotDetectionInterval(): void {
    if (this.slotDetectionIntervalId !== null) {
      return;
    }

    this.slotDetectionAttempts = 0;
    this.slotDetectionIntervalId = setInterval(() => {
      this.slotDetectionAttempts++;

      if (this.slotDetectionAttempts >= this.maxSlotDetectionAttempts) {
        this.clearSlotDetectionInterval();
        logger.warn(
          `Failed to detect slot for adUnit ${this.adUnitCode} after ${this.maxSlotDetectionAttempts} attempts`
        );
        return;
      }

      this.determineSlot();
    }, this.slotDetectionIntervalMs);
  }

  private clearSlotDetectionInterval(): void {
    if (this.slotDetectionIntervalId !== null) {
      clearInterval(this.slotDetectionIntervalId);
      this.slotDetectionIntervalId = null;
    }
  }

  public destroy(): void {
    this.clearSlotDetectionInterval();
  }

  public hasValidSlot(): boolean {
    return this.slot !== null;
  }

  public get adUnit(): PrebidAdUnit {
    return this._adUnit;
  }

  public get adUnitCode(): string {
    return this.adUnitTransformer(this._adUnit.code);
  }

  public get currentOpportunity(): Opportunity | null {
    return this._opportunities.length > 0
      ? this._opportunities[this._opportunities.length - 1]
      : null;
  }

  public get facts(): Facts {
    return clone(this._facts);
  }

  /**
   * Returns the native slot object of the tagging system (e.g., GPT slot object).
   */
  public get nativeSlotObject(): any {
    return this.slot;
  }

  public get domElement(): HTMLElement | null {
    return this.wrappingDomElement;
  }

  public get visibility(): Visibility {
    return this._visibility;
  }

  public get supportedSizes(): PlacementSize[] {
    return this._supportedSizes;
  }

  public isLinkedToAdUnit(adUnitCode: string): boolean {
    if (this.adUnitTransformer(adUnitCode) === this.adUnitCode) {
      return true;
    }
    return false;
  }

  public newOpportunity(opportunity: Opportunity): void {
    // TODO: Check slot validity again.
    logger.info(`New opportunity ${opportunity.id} for placement ${this.adUnitCode}`);
    this._opportunities.push(opportunity);
    if (this._opportunities.length > MAX_STORED_OPPORTUNITIES) {
      this._opportunities.shift();
    }
    this.determineVisibility();
    this.setFacts();
    this.renderedAdData = null;
    this.slotRenderedForOpportunity = false;
    this.enablePrebidRenderListenerForOpportunity = false;
    this.prebidAdRenderedForOpportunity = false;
    this.slotViewedForOpportunity = false;
    this.adInteractionLoggedForOpportunity = false;
    this.partialAdData = null;
    this.viewableImpressionMissed = false;
  }

  public updateCampaigns(campaignData: CampaignData[]): void {
    this.campaignData = campaignData;
    this._campaigns = this.campaignData.map((data) => new Campaign(data, this.facts));
    logger.debug(
      `Placement campaigns updated with ${this._campaigns.length} campaigns for adUnit ${this.adUnitCode}`
    );
  }

  public setKeyValues(placementKeyValues: PlacementAssignedKeyValues): void {
    if (placementKeyValues.kvs) {
      this._keyValues = [...placementKeyValues.kvs];
    }
    // Set key values on campaigns if ad-specific key values are provided
    if (placementKeyValues.ads) {
      this._campaigns.forEach((campaign) => {
        campaign.adKeyValues = placementKeyValues.ads!;
      });
    }
  }

  public hasAnyCampaignForTypes(campaignTypes: CampaignType[]): boolean {
    return this._campaigns.some((campaign) => campaignTypes.includes(campaign.type));
  }

  public getKeyValuesForCampaignType(campaignTypes: CampaignType[]): KeyValue[] {
    const allKeyValues: KeyValue[] = [];
    allKeyValues.push(...clone(this._keyValues));

    this._campaigns
      .filter((campaign) => campaignTypes.includes(campaign.type))
      .forEach((campaign) => {
        allKeyValues.push(...campaign.keyValues);
      });

    const mergedKeyValues: KeyValue[] = [];

    allKeyValues.forEach((kv) => {
      const existingKv = mergedKeyValues.find((existingKv) => existingKv.key === kv.key);
      if (!existingKv) {
        mergedKeyValues.push({ ...kv });
      } else {
        const existingValues = existingKv.value.split(',');
        if (!existingValues.includes(kv.value)) {
          existingKv.value += `,${kv.value}`;
        }
      }
    });

    return mergedKeyValues;
  }

  public getEligibleAdIdsByCampaign(campaignTypes: CampaignType[]): CampaignEligibleAds[] {
    const campaigns: CampaignEligibleAds[] = [];

    this._campaigns
      .filter((campaign) => campaignTypes.includes(campaign.type))
      .forEach((campaign) => {
        const campaignEligibleAdIds: string[] = campaign.getEligibleAdIds();
        if (campaignEligibleAdIds.length > 0) {
          campaigns.push({
            id: campaign.id,
            edids: campaignEligibleAdIds,
          });
        }
      });

    return campaigns;
  }

  public getAdKeyValuesByAd(): AdAssignedKeyValues[] {
    const adKeyValues: AdAssignedKeyValues[] = [];

    this._campaigns.forEach((campaign) => {
      adKeyValues.push(...campaign.keyValuesByAd);
    });

    return adKeyValues;
  }

  public getAdKeyValuesByAdForCampaignTypes(campaignTypes: CampaignType[]): AdAssignedKeyValues[] {
    const adKeyValues: AdAssignedKeyValues[] = [];

    this._campaigns
      .filter((campaign) => campaignTypes.includes(campaign.type))
      .forEach((campaign) => {
        adKeyValues.push(...campaign.keyValuesByAd);
      });

    return adKeyValues;
  }

  public hasDealActivated(dealId: string): boolean {
    return this._campaigns.some((campaign) => campaign.deals.includes(dealId));
  }

  public getLearningCampaignDeliveryId(campaignType: CampaignType): string | undefined {
    const learningCampaign = this._campaigns.find((campaign) => campaign.type === campaignType);
    return learningCampaign?.adDeliveryIds[0];
  }

  public getDealCampaignDeliveryId(dealId: string): string | undefined {
    const dealCampaign = this._campaigns.find((campaign) => campaign.deals.includes(dealId));
    return dealCampaign?.adDeliveryIds[0];
  }

  public getMatchingAdDeliveryId(lineId: string): string | undefined {
    for (const campaign of this._campaigns) {
      const deliveryId = campaign.getMatchingAdDeliveryId(lineId);
      if (deliveryId) {
        return deliveryId;
      }
    }
    return undefined;
  }

  public getUntaggedMonitoredLineIds(): string[] {
    const untaggedLineIds: string[] = [];

    this._campaigns.forEach((campaign) => {
      if (campaign.type === CampaignType.DIRECT) {
        untaggedLineIds.push(...campaign.linkedExternalLineIds);
      }
    });

    return untaggedLineIds;
  }

  public handleTaggedAdDelivery(deliveryId: string, impressionId: string): void {
    if (!this.partialAdData) {
      logger.warn(
        `No partial ad data found for placement ${this.adUnitCode} when handling TaggedAdServed`
      );
      return;
    }

    this.renderedAdData = {
      ...this.partialAdData,
      responseIdentifier: impressionId,
    } as RenderedAdData;
    this.partialAdData = null;

    let keyValues: KeyValue[] = [];
    for (const campaign of this._campaigns) {
      const campaignKeyValues = campaign.getAdKeyValues(deliveryId, AdIdType.DELIVERY_ID);
      if (campaignKeyValues.length > 0) {
        keyValues = campaignKeyValues;
        break;
      }
    }

    this.dispatchDirectServedAdImpression(deliveryId, keyValues || undefined);

    if (this.viewableImpressionMissed) {
      this.dispatchViewableImpression();
      this.viewableImpressionMissed = false;
    }
  }

  protected getSiteConfig(): SiteConfig {
    return this.siteConfig;
  }

  protected isVisible(visible: boolean = false): void {
    this._visibility = visible ? Visibility.VISIBLE : Visibility.NOT_VISIBLE;
  }

  protected addSupportedSize(size: PlacementSize): void {
    if (!size) {
      return;
    }
    if (
      !this.supportedSizes.some(
        // @ts-ignore
        (existingSize) => existingSize.width === size.width && existingSize.height === size.height
      )
    ) {
      this.supportedSizes.push(size);
    }
  }

  protected getSmallestSupportedSize(): PlacementSize {
    if (this.supportedSizes.length === 0) {
      return null;
    }
    let smallest = this.supportedSizes[0];
    this.supportedSizes.forEach((size) => {
      if (size!.width * size!.height < smallest!.width * smallest!.height) {
        smallest = size;
      }
    });
    return smallest;
  }

  protected addPrebidAdRenderedListener(): void {
    const pbjs = getGlobalPrebidObj();
    if (pbjs) {
      const prebidAdRenderedEventHandler = (data: PrebidAdRenderSucceededData) => {
        if (!this.enablePrebidRenderListenerForOpportunity) {
          return;
        }
        if (this.isLinkedToAdUnit(data.bid.adUnitCode) && !this.prebidAdRenderedForOpportunity) {
          let deltaSeconds = Math.min(Math.round(this.currentOpportunity!.age / 1000), 20);
          debugPixel({
            en: 'prebidAdRendered',
            adUnitCode: this.adUnitCode,
            renderTimeDelta: String(deltaSeconds),
          });
          logger.debug(
            `Prebid AdRenderSucceeded event received for ${this.adUnitCode}, data: ${JSON.stringify(data.bid)}`
          );

          this.prebidAdRenderedForOpportunity = true;
          pbjs.offEvent('adRenderSucceeded', prebidAdRenderedEventHandler);

          this.renderedAdData = {
            advertiserDomains: data.bid.meta?.advertiserDomains ?? ['UNKNOWN'],
            responseIdentifier: data.bid.transactionId ?? generateUUID(),
            isProgrammaticBuy: true,
            nodalsDealId:
              data.bid.dealId && this.hasDealActivated(data.bid.dealId)
                ? data.bid.dealId
                : undefined,
          };
          const deliveryId = this.renderedAdData.nodalsDealId
            ? this.getDealCampaignDeliveryId(this.renderedAdData.nodalsDealId)
            : this.getLearningCampaignDeliveryId(CampaignType.LEARNING_PROGRAMMATIC);
          if (deliveryId) {
            this.dispatchProgrammaticServedAdImpression(deliveryId);
          }
        }
      };
      pbjs.onEvent('adRenderSucceeded', prebidAdRenderedEventHandler);

      const prebidAdRenderedFailedEventHandler = (data: PrebidAdRenderFailedData) => {
        if (this.isLinkedToAdUnit(data.bid.adUnitCode) && !this.prebidAdRenderedForOpportunity) {
          debugPixel({
            en: 'prebidAdRenderFailed',
            adUnitCode: this.adUnitCode,
          });
        }
      };
      pbjs.onEvent('adRenderFailed', prebidAdRenderedFailedEventHandler);
    } else {
      logger.info(
        `Unable to add Prebid listeners for ${this.adUnitCode}: Global Prebid object not found`
      );
    }
  }

  protected dispatchDirectServedAdImpression(
    deliveryId: string,
    keyValues: KeyValue[] | undefined = undefined
  ): void {
    const adData: RenderedAdData = this.renderedAdData as RenderedAdData;
    const currentOpportunity = this.currentOpportunity;
    if (adData && currentOpportunity) {
      this.impressionDeliveryId = deliveryId;
      this.dispatch('DirectServedAdImpression', {
        id: adData.responseIdentifier,
        opportunityId: currentOpportunity.id,
        deliveryId: deliveryId,
        advertiserId: adData.advertiserId as string,
        lineItemId: adData.lineItemId as string,
        creativeId: adData.creativeId,
        size: adData.size as PlacementSize,
        kvs: keyValues,
      });
    }
  }

  protected dispatchProgrammaticServedAdImpression(deliveryId: string): void {
    const adData: RenderedAdData = this.renderedAdData as RenderedAdData;
    const currentOpportunity = this.currentOpportunity;
    if (adData && currentOpportunity) {
      this.impressionDeliveryId = deliveryId;
      this.dispatch('ProgrammaticServedAdImpression', {
        id: adData.responseIdentifier,
        opportunityId: currentOpportunity.id,
        deliveryId: deliveryId,
        advertiserDomains: adData.advertiserDomains as string[],
        size: adData.size as PlacementSize,
        nodalsDealId: adData.nodalsDealId,
      });
      this.addAdInteractionListener();
    }
  }

  protected dispatchViewableImpression(): boolean {
    if (this.slotViewedForOpportunity) {
      return false;
    }

    // If we don't have rendered ad data yet, check if we're waiting for a Nodals ad
    if (!this.renderedAdData) {
      if (this.partialAdData) {
        // We're waiting for TaggedAdServed - mark that we missed the viewable impression timing
        this.viewableImpressionMissed = true;
        logger.debug(
          `Viewable impression timing missed for placement ${this.adUnitCode}, will dispatch after TaggedAdServed`
        );
      }
      return false;
    }
    const currentOpportunity = this.currentOpportunity;
    if (currentOpportunity && this.impressionDeliveryId) {
      this.dispatch('ViewableImpression', {
        impressionId: this.renderedAdData?.responseIdentifier ?? generateUUID(),
        opportunityId: currentOpportunity.id,
        deliveryId: this.impressionDeliveryId as string,
        advertiserId: this.renderedAdData?.advertiserId ?? undefined,
        advertiserDomains: this.renderedAdData?.advertiserDomains ?? undefined,
        lineItemId: this.renderedAdData?.lineItemId ?? undefined,
        nodalsDealId: this.renderedAdData?.nodalsDealId ?? undefined,
      });
      this.slotViewedForOpportunity = true;
      return true;
    }
    return false;
  }

  protected dispatchAdInteraction(): boolean {
    if (this.adInteractionLoggedForOpportunity) {
      return false;
    }
    const currentOpportunity = this.currentOpportunity;
    if (currentOpportunity && this.impressionDeliveryId) {
      logger.info(`Dispatching AdInteraction for ${this.adUnitCode}`);
      this.dispatch('AdInteraction', {
        impressionId: this.renderedAdData?.responseIdentifier ?? generateUUID(),
        opportunityId: currentOpportunity.id,
        deliveryId: this.impressionDeliveryId as string,
        advertiserId: this.renderedAdData?.advertiserId ?? undefined,
        advertiserDomains: this.renderedAdData?.advertiserDomains ?? undefined,
        lineItemId: this.renderedAdData?.lineItemId ?? undefined,
        nodalsDealId: this.renderedAdData?.nodalsDealId ?? undefined,
      });
      this.adInteractionLoggedForOpportunity = true;
      return true;
    }
    return false;
  }

  protected handleRenderedAdData(adData: RenderedAdData): void {
    const isUntaggedMonitoredLine = this.getUntaggedMonitoredLineIds().includes(
      adData.lineItemId as string
    );
    const isNodalsAdvertiser = adData.advertiserId === this.getSiteConfig().nodalsAdvertiserId;
    if (isUntaggedMonitoredLine || isNodalsAdvertiser) {
      debugPixel({
        en: 'slotRenderedMonitoredLineOrNodalsAdvertiser',
        adUnitCode: this.adUnitCode,
        lineId: adData.lineItemId as string,
        advertiserId: adData.advertiserId as string,
      });
      if (isNodalsAdvertiser) {
        logger.debug(
          `Expecting tagged ad for Nodals advertiser ${adData.advertiserId} for ${this.adUnitCode}`
        );
        this.partialAdData = {
          advertiserId: adData.advertiserId,
          lineItemId: adData.lineItemId,
          creativeId: adData.creativeId,
          isProgrammaticBuy: false,
          size: adData.size,
        };
        return;
      } else {
        logger.info(
          `Untagged monitored line: ${adData.lineItemId} response for ${this.adUnitCode}.`
        );
        this.renderedAdData = adData;
        const deliveryId = this.getMatchingAdDeliveryId(adData.lineItemId as string);
        if (deliveryId) {
          this.dispatchDirectServedAdImpression(deliveryId);
          // Interaction listener added here to prevent double-counting for
          // TaggedAdServed scenarios.
          this.addAdInteractionListener();
        }
      }
    } else if (
      !this.getSiteConfig().directLearningAdvertiserIgnoreList.includes(
        adData.advertiserId as string
      )
    ) {
      logger.info(`Direct learning opportunity for ${this.adUnitCode}.`);
      this.renderedAdData = adData;
      const deliveryId = this.getLearningCampaignDeliveryId(CampaignType.LEARNING_DIRECT);
      if (deliveryId) {
        this.dispatchDirectServedAdImpression(deliveryId);
        this.addAdInteractionListener();
      }
    } else {
      logger.info(`Programmatic learning opportunity for ${this.adUnitCode}.`);
      this.enablePrebidRenderListenerForOpportunity = true;
    }
  }

  /**
   * Method to be implemented by subclasses to determine additional
   * sizes supported by the placement beyond those defined in the Prebid configuration.
   */
  protected abstract setSupportedSizes(): void;
  protected abstract determineVisibility(): void;

  /**
   * Method to be implemented by subclasses to set the native slot
   * object of the tagging system (e.g., GPT). a slot property
   * _must_ be set by the implementation in order for the placement
   * to be considered valid.
   */
  protected abstract setSlot(): void;
  protected abstract setWrappingDomElement(): void;

  /**
   * Method to be implemented by subclasses to add event listeners
   * relevant to the specific tagging system (e.g., GPT).
   * This is invoked after the native slot has been determined.
   */
  protected abstract addListeners(): void;
  protected abstract addAdInteractionListener(): void;

  private initialiseCampaigns(): void {
    this._campaigns = this.campaignData.map((data) => new Campaign(data, this.facts));
    logger.debug(
      `Placement initialised with ${this._campaigns.length} campaigns for adUnit ${this.adUnitCode}`
    );
  }

  private setFacts(): void {
    this._facts['placement.visible'] = this.visibility;
    if (this.supportedSizes.length === 0) {
      this._facts['placement.sizes'] = [DIMENSIONS_UNKNOWN];
      return;
    }
    this._facts['placement.sizes'] = this.supportedSizes.map((size: PlacementSize) => {
      if (!size) {
        return DIMENSIONS_UNKNOWN;
      }
      return `${size.width}x${size.height}`;
    });
  }

  /**
   * Initialises the supported sizes based on Prebid configuration.
   * If no sizes are found, the supported sizes will remain empty.
   */
  private initialiseSupportedSizes(): void {
    if (this.adUnit.mediaTypes?.banner) {
      this.adUnit.mediaTypes.banner.sizes.forEach((size) => {
        this.addSupportedSize({ width: size[0], height: size[1] });
      });
    } else if (this.adUnit.mediaTypes?.video) {
      const video = this.adUnit.mediaTypes.video;
      if (Array.isArray(video.playerSize) && video.playerSize.length === 2) {
        this.addSupportedSize({
          width: video.playerSize[0] as number,
          height: video.playerSize[1] as number,
        });
      }
    }
  }
}

export { Placement };
